import { Component, DoCheck, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { ScheduledEvent, ScheduledEventReservation } from '../../../../../../../../../../../models/scheduled-event';
import { EventType, Roster } from '../../../../../../../../../../../models/event-type';
import { Event } from '../../../../../../../../../../../models/event';
import {
  batchAll,
  CallbackResult,
  CallbackResultStatus,
  dateStringFromISO8601,
  getAge,
  getISO8601DateString,
  propExistsAndIsNonEmpty,
  timeStringFromISO8601,
} from '../../../../../../../../../../../utils';
import { ScheduledEventService } from '../../../../../../../../../../../services/scheduled-event/scheduled-event.service';
import { EventService } from '../../../../../../../../../../../services/event/event.service';
import { EventTypeService } from '../../../../../../../../../../../services/event-type/event-type.service';
import { EventTypeStatus, ScheduleType } from '../../../../../../../../../../../models/event-type-enums';
import { eventFullDueToSecondaryCapacity } from '../../../../../../../../../../../utils-models';
import {
  blankInviteExpiry,
  InviteExpiry,
  inviteExpiryRequestFields,
  inviteExpiryValidationError
} from '../../../../../../../../../../../models/invite-expiry';
import { AgeRestrictionBypass, eventAgeAsOfDate } from '../../../../../../../../../../../models/age-restriction';


@Component({
  selector: 'app-invite-participants-to-another-event-modal',
  templateUrl: './invite-participants-to-another-event-modal.component.html',
  styleUrls: ['./invite-participants-to-another-event-modal.component.css']
})
export class InviteParticipantsToAnotherEventModalComponent implements OnInit, DoCheck {
  @Input() scheduledEvent: ScheduledEvent;
  @Input() savedCallback: (result: CallbackResult) => undefined;

  public eventTypeList: EventType[];
  public selectedEventType: EventType = null;
  public selectedDate: string;
  public scheduledEventsForDate: ScheduledEvent[] = [];
  public rosters: Roster[] = [];
  public selectedEvent: Event = null;
  public selectedRoster: Roster = null;
  public startDate = '';

  public selectedCustomerIds: number[] = [];
  public inviteNote = '';
  public expiry: InviteExpiry = blankInviteExpiry();
  public sendInviteToParentsAndGuardians = false;

  public readonly ageRestriction = new AgeRestrictionBypass();

  public errorMessage = '';
  public serverError: number = null;

  public loadingEventTypes = false;
  public loadingEventsForDate = false;
  public loadingRosters = false;
  public loadingSelectedEvent = false;
  public loadingSave = false;
  public numberSaved = 0;

  public readonly timeStringFromISO8601 = timeStringFromISO8601;
  public readonly eventFullDueToSecondaryCapacity = eventFullDueToSecondaryCapacity;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly getAge = getAge;
  public readonly propExistsAndIsNonEmpty = propExistsAndIsNonEmpty;
  public readonly ScheduleType = ScheduleType;

  // The number of loadEventsForDate calls. Used to ensure that only the most
  // recent result is displayed, in the event of concurrent requests (due to,
  // e.g., rapid pagination).
  private loadEventsForDateRequestCount = 0;

  constructor(
    public activeModal: NgbActiveModal,
    private eventTypeService: EventTypeService,
    private scheduledEventService: ScheduledEventService,
    private eventService: EventService,
  ) {}

  ngOnInit() {
    this.selectedDate = getISO8601DateString(new Date());

    this.loadEventTypes();
  }

  ngDoCheck() {
    if ((this.selectedEventType === null) ||
        (this.selectedEventType.schedule_type === ScheduleType.rolling) ||
        (this.selectedEvent === null)) {
      this.ageRestriction.refresh('', [], null);
      return;
    }

    const seenCustomerIds = new Set<number>();
    const participants = this.getFilteredParticipants()
      .filter(res => {
        if (!res.customer_id || !this.selectedCustomerIds.includes(res.customer_id)) { return false; }
        if (seenCustomerIds.has(res.customer_id)) { return false; }
        seenCustomerIds.add(res.customer_id);
        return true;
      })
      .map(res => ({
        key: res.customer_id.toString(),
        name: res.first_name + ' ' + res.last_name,
        birthday: res.birthday,
      }));

    // The event's own type, since changing the type picker does not clear an
    // already-selected event.
    const eventType = (this.eventTypeList || []).find(et => et.id === this.selectedEvent.event_type_id) ||
      this.selectedEventType;

    this.ageRestriction.refresh(
      'event-' + this.selectedEvent.id.toString(),
      participants,
      eventType,
      eventAgeAsOfDate(this.selectedEvent)
    );
  }

  public handleSelectedEventType() {
    if (this.selectedEventType === null) { return; }

    if (this.selectedEventType.schedule_type === ScheduleType.rolling) {
      this.loadRostersForEventType();
    } else {
      this.loadEventsForDate();
    }
  }

  public loadEventsForDate() {
    this.scheduledEventsForDate = [];

    if (!this.selectedEventType && !this.selectedDate) { return; }

    this.loadingEventsForDate = true;

    // Increment the request count and save a local copy.
    this.loadEventsForDateRequestCount++;
    const thisRequestCount = this.loadEventsForDateRequestCount;

    this.scheduledEventService
      .getScheduledEvents(
        this.selectedDate,
        this.selectedEventType.id,
        false
      )
      .then(scheduledEvents => {
        // Only update the events list if there has not been a more recent
        // request initiated.
        if (thisRequestCount !== this.loadEventsForDateRequestCount) {
          return;
        }

        this.scheduledEventsForDate = scheduledEvents;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => {
        if (thisRequestCount === this.loadEventsForDateRequestCount) {
          this.loadingEventsForDate = false;
        }
      });
  }

  public loadSelectedEvent(selectedEventId: number) {
    this.loadingSelectedEvent = true;

    this.eventService
      .getEvent(selectedEventId)
      .then(event => {
        this.selectedEvent = event;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => {
        this.loadingSelectedEvent = false;
      });
  }

  public getFilteredParticipants(): ScheduledEventReservation[] {
    return this.scheduledEvent.reservations
      .filter(res => !res.is_cancelled);
  }

  public toggleSelectedCustomerId(customerId: number) {
    if (this.selectedCustomerIds.includes(customerId)) {
      this.selectedCustomerIds.splice(this.selectedCustomerIds.indexOf(customerId), 1);
    } else {
      this.selectedCustomerIds.push(customerId);
    }
  }

  public allCustomersAreSelected(): boolean {
    for (const res of this.getFilteredParticipants()) {
      if (res.customer_id && !this.selectedCustomerIds.includes(res.customer_id)) {
        return false;
      }
    }

    return true;
  }

  public setAllCustomersSelected() {
    this.selectedCustomerIds = this.getFilteredParticipants()
      .filter(res => !!res.customer_id)
      .map(res => res.customer_id);
  }

  public getLoadingProgressPercentageStr() {
    return Math.ceil((this.numberSaved / this.selectedCustomerIds.length) * 100).toString();
  }

  public sendInvitesAllowed(): boolean {
    if (this.selectedEventType === null) { return false; }
    if (this.selectedCustomerIds.length === 0) { return false; }
    if (inviteExpiryValidationError(this.expiry, this.expiryTimeZone()) !== null) { return false; }
    if (this.ageRestriction.blocksSend()) { return false; }

    if (this.selectedEventType.schedule_type === ScheduleType.rolling) {
      return (this.selectedRoster !== null);
    } else {
      return (this.selectedEvent !== null);
    }
  }

  public expiryTimeZone(): string | null {
    if (this.selectedEventType === null) { return null; }

    if (this.selectedEventType.schedule_type === ScheduleType.rolling) {
      return this.selectedRoster ? this.selectedRoster.time_zone : null;
    }
    return this.selectedEvent ? this.selectedEvent.time_zone : null;
  }

  public handleSendInvites() {
    this.errorMessage = '';
    this.loadingSave = true;

    const expiryFields = inviteExpiryRequestFields(this.expiry);

    this.numberSaved = 0;
    let successfulCount = 0;
    // The server's refusal reasons, keyed to the customers they apply to:
    // reasons differ per customer (PRO-1457), and the server's sentence is
    // written for a single participant.
    const failedNamesByReason = new Map<string, string[]>();

    const customerIdHandler = (customerId: number): Promise<void> => {
      // Stop early if an error message or server error is produced.
      if ((this.errorMessage !== '') || (this.serverError !== null)) {
        return Promise.resolve();
      }

      let invitePromise: Promise<void>;

      if (this.selectedEventType.schedule_type === ScheduleType.rolling) {
        invitePromise = this.scheduledEventService
          .inviteCustomerToJoinRoster(
            this.selectedRoster.id,
            customerId,
            this.startDate,
            expiryFields.hoursToAccept,
            this.inviteNote,
            this.sendInviteToParentsAndGuardians,
            expiryFields.newDeadlineDate,
            expiryFields.newDeadlineTime
          );
      } else {
        invitePromise = this.scheduledEventService
          .inviteCustomerToBookEvent(
            this.selectedEvent.id,
            customerId,
            expiryFields.hoursToAccept,
            this.inviteNote,
            this.sendInviteToParentsAndGuardians,
            expiryFields.newDeadlineDate,
            expiryFields.newDeadlineTime,
            this.ageRestriction.codesFor(customerId.toString())
          );
      }

      return invitePromise
        .then(() => { successfulCount++; })
        .catch(reason => {
          if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to perform this action.';
          } else if (reason.status === 400) {
            // Not counted as successful; the reason is reported in the summary
            // below. Only a string body is a refusal message to show.
            if (typeof reason.error === 'string') {
              const named = failedNamesByReason.get(reason.error) || [];
              named.push(this.getCustomerName(customerId));
              failedNamesByReason.set(reason.error, named);
            }
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.numberSaved++; });
    };

    batchAll(
      4,
      this.selectedCustomerIds,
      customerIdHandler
    )
      .then(() => {
        if (this.errorMessage !== '') { return; }

        const failedCount = this.selectedCustomerIds.length - successfulCount;

        let successMessage = (
          successfulCount.toString() +
          ' customer' +
          ((successfulCount === 1) ? ' was' : 's were') +
          ' invited successfully.'
        );
        if (failedCount > 0) {
          successMessage += (
            ' ' +
            failedCount.toString() +
            ' customer' +
            ((failedCount === 1) ? '' : 's') +
            ' could not be invited'
          );
          const reasonsText = this.buildFailureReasonsText(
            failedNamesByReason
          );
          if (reasonsText !== '') {
            successMessage += '. ' + reasonsText;
          } else {
            successMessage += (
              ', most likely because they have already been invited to the ' +
              ((this.selectedEventType.schedule_type === ScheduleType.rolling) ? 'roster.' : 'event.')
            );
          }
        }

        this.savedCallback({
          text: successMessage,
          status: (successfulCount > 0) ? CallbackResultStatus.success : CallbackResultStatus.error
        });
        this.activeModal.close();
      })
      .finally(() => { this.loadingSave = false; });
  }

  /**
   * The participant's name, for reporting which customers an invite refusal
   * applies to. Falls back to the ID, which is better than an empty string in
   * a message a staff member has to act on.
   */
  private getCustomerName(customerId: number): string {
    const reservation = this.scheduledEvent.reservations
      .find(res => res.customer_id === customerId);

    if (!reservation) { return 'Customer ' + customerId.toString(); }

    return reservation.first_name + ' ' + reservation.last_name;
  }

  /**
   * Render the refusal reasons the server gave, each prefixed with the
   * customers it applies to. The server writes these for one participant
   * ("This participant has..."), so a bare list of them in a bulk summary
   * would not say who any of them is about.
   */
  private buildFailureReasonsText(failedNamesByReason: Map<string, string[]>): string {
    const parts: string[] = [];

    failedNamesByReason.forEach((names, reason) => {
      parts.push(names.join(', ') + ': ' + reason);
    });

    return parts.join(' ');
  }

  private loadEventTypes() {
    this.loadingEventTypes = true;

    this.eventTypeService
      .getEventTypes(
        1,
        3000,
        {status: EventTypeStatus.active}
      )
      .then(response => {
        this.eventTypeList = response.results;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => {
        this.loadingEventTypes = false;
      });
  }

  private loadRostersForEventType() {
    this.loadingRosters = true;

    this.eventTypeService
      .getRosters(
        1,
        3000,
        {event_type_id: this.selectedEventType.id}
      )
      .then(result => {
        this.rosters = result.results;
        if (this.rosters.length === 1) {
          this.selectedRoster = this.rosters[0];
        }
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingRosters = false; });
  }
}
